import React, { useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { CreatePurchaseOrderForm } from './CreatePurchaseOrderForm';
import { purchaseOrderService } from '../../services/purchaseOrderService';
import type { PurchaseOrderFormData } from '../../types/purchaseOrder';
import { ArrowLeft } from 'lucide-react';
import { useLanguage } from '../../features/language/useLanguage';
import { useOnboardingStore } from '../../store/onboardingStore';

interface LocationState {
  createPO?: boolean;
  initialItem?: {
    inventoryItemId: string;
    quantity: number;
    unitPrice: number;
    name: string;
  };
}

export const CreatePurchaseOrderPage: React.FC = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const state = location.state as LocationState | null;
  const onboardingActive = useOnboardingStore((s) => s.state?.status === 'in_progress');
  const onboardingStep2 = useOnboardingStore((s) => s.state?.currentStep === 2);

  const initialData = useMemo<PurchaseOrderFormData | undefined>(() => {
    if (state?.createPO && state?.initialItem) {
      return {
        supplier: '',
        orderDate: new Date().toISOString().split('T')[0],
        expectedDeliveryDate: new Date(new Date().setDate(new Date().getDate() + 1)).toISOString().split('T')[0],
        status: 'draft',
        paymentType: t('purchaseOrder.filters.options.bankTransfer'),
        notes: '',
        internalNotes: '',
        items: [{
          inventoryItemId: state.initialItem.inventoryItemId,
          quantity: state.initialItem.quantity,
          unitPrice: state.initialItem.unitPrice,
          name: state.initialItem.name,
          weightReceived: state.initialItem.quantity,
          itemCode: '', // Will be populated by form logic if existing item
          sampleItem: false
        }]
      };
    }
    return undefined;
  }, [state, t]);

  const handleCreatePO = async (data: PurchaseOrderFormData) => {
    try {
      const created = await purchaseOrderService.create(data, {
        onboardingSample: onboardingActive && onboardingStep2,
      });
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['purchase-orders'] }),
        queryClient.invalidateQueries({ queryKey: ['lots'] }),
        queryClient.invalidateQueries({ queryKey: ['samples'] }),
      ]);
      // Dispatch before navigating away so the onboarding overlay can capture
      // the new ids and advance to step 3.
      if (onboardingActive && onboardingStep2) {
        window.dispatchEvent(
          new CustomEvent('qrema:purchase-order-created', {
            detail: {
              poId: created.id,
              greenLotId: created.items?.[0]?.lotId ?? '',
            },
          }),
        );
      }
      navigate('/purchase-orders');
    } catch (err) {
      console.error('Failed to create PO:', err);
      // Error is handled in the form usually, or we can show toast here
      throw err;
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate('/purchase-orders')}
          className="p-2 hover:bg-gray-100 rounded-full transition-colors"
        >
          <ArrowLeft className="h-6 w-6 text-gray-600" />
        </button>
        <h1 className="text-2xl font-semibold text-gray-900">{t('purchaseOrder.form.submit.create')}</h1>
      </div>

      <div className="bg-white rounded-lg shadow-sm p-6">
        <CreatePurchaseOrderForm
          initialData={initialData}
          onSubmit={handleCreatePO}
          onCancel={() => navigate('/purchase-orders')}
        />
      </div>
    </div>
  );
};
